import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import DisplayDate from '../../components/DisplayDate';
import Button from '../../components/Button';
import DisplayDragIcon from '../../components/DisplayDragIcon';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { HelpDeskAdminDashboardHelpDeskItem, HelpDeskReorderCommand } from '../../api/models';

const ContentHelpDesk: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to control Modal
	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to store Help Desk Id
	const [helpDeskId, setHelpDeskId] = React.useState<string>('');

	// State to store Child Safe Help Desk title
	const [helpDeskTitle, setHelpDeskTitle] = React.useState<string>('');

	// Set up Data state
	const [helpDeskData, setHelpDeskData] = React.useState<HelpDeskAdminDashboardHelpDeskItem[]>([]);

	const getHelpDeskAdminDashboard = async () => {
		setIsLoading(true);

		const response = await apibridge.getHelpDeskAdminDashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = [] } = response;

			setIsLoading(false);

			setHelpDeskData(result);
		}
	};

	React.useEffect(() => {
		getHelpDeskAdminDashboard();
	}, []);

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(helpDeskData);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setHelpDeskData(items);

		const data: HelpDeskReorderCommand = { id: reorderedItem.id, order: result.destination.index + 1 };

		apibridge.postHelpDeskReorder(data);
	};

	const removeHandler = async (item: HelpDeskAdminDashboardHelpDeskItem) => {
		setHelpDeskId(item.id || '');

		setHelpDeskTitle(item.title || '');

		setShowRemoveModal(true);
	};

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="single-btn-right-container border">
									<Link className="btn-primary btn-right" to="/content-help-desk-edit">
										<PlusIcon className="icon-link" /> Add new
									</Link>
								</div>

								<section className="info-section mtop-0">
									{helpDeskData && helpDeskData.length > 0 ? (
										<DragDropContext onDragEnd={onDragEndHandler}>
											<Droppable droppableId="contentHelpDeskItem">
												{(provided: DroppableProvided) => (
													<ul {...provided.droppableProps} ref={provided.innerRef}>
														{helpDeskData.map((item, index) => {
															return (
																<Draggable
																	key={index}
																	draggableId={index.toString()}
																	index={index}
																	isDragDisabled={isOrganisationActive === false}
																>
																	{(provided) => (
																		<li
																			className="mbtom-16 "
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			{...provided.dragHandleProps}
																		>
																			<div className="row detail-row helpdesk">
																				<div className="col-auto col-sm-8 ">
																					<div className="row detail-info">
																						<div className="col-10 col-md-7 detail-name helpdesk">{item.title}</div>

																						<div className="col-10 col-md-5 text-category-detail">
																							{item.updated && <>Last updated &nbsp; &nbsp;</>}

																							{item.updated && (
																								<span className="text-subtitle">
																									<DisplayDate>{item.updated}</DisplayDate>
																								</span>
																							)}
																						</div>
																					</div>
																					<div className="d-block d-sm-none">
																						<Link
																							className="btn-text remove link"
																							to={`/content-help-desk-edit/${item.id}`}
																						>
																							Edit
																						</Link>

																						<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																							Remove
																						</Button>
																					</div>
																				</div>

																				<div className="drag-icon-container d-sm-none">
																					<DisplayDragIcon index={index} array={helpDeskData} />
																				</div>

																				<div className="d-none d-sm-flex col-sm-auto pdr-0 link-group content-helpdesk-page">
																					<Link
																						className="btn-text remove link"
																						to={`/content-help-desk-edit/${item.id}`}
																					>
																						Edit
																					</Link>

																					<Button className="btn-text link pdr-0" onClick={() => removeHandler(item)}>
																						Remove
																					</Button>

																					<div className="drag-icon-container d-none d-sm-inline-block">
																						<DisplayDragIcon index={index} array={helpDeskData} />
																					</div>
																				</div>
																			</div>
																		</li>
																	)}
																</Draggable>
															);
														})}
														{provided.placeholder}
													</ul>
												)}
											</Droppable>
										</DragDropContext>
									) : (
										<div className="detail-prompt">
											Information in this section is coming soon. In the meantime feel free to{' '}
											<a href={`${websiteUrl}/contact-us`} target="_blank">
												contact us
											</a>
											.
										</div>
									)}
								</section>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}

			<GlobalModal isOpen={showRemoveModal} setIsOpen={setShowRemoveModal} titleText="Remove Help Desk item">
				<RemoveModal
					helpDeskData={helpDeskData}
					setHelpDeskData={setHelpDeskData}
					getHelpDeskAdminDashboard={getHelpDeskAdminDashboard}
					helpDeskId={helpDeskId}
					helpDeskTitle={helpDeskTitle}
				/>
			</GlobalModal>
		</div>
	);
};

export default ContentHelpDesk;
